import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Nav from "./Nav";
import Footer from "./Footer";
import BlogCover from "./BlogCover";
import { POSTS, ALL_CATEGORIES } from "./posts";
import { Toaster } from "sonner";

export default function BlogPage() {
  const [activeCat, setActiveCat] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POSTS.map((p, i) => ({ ...p, index: i })).filter((p) => {
      const catMatch = activeCat === "All" || p.categories.includes(activeCat);
      const qMatch =
        !q ||
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.categories.some((c) => c.toLowerCase().includes(q));
      return catMatch && qMatch;
    });
  }, [activeCat, query]);

  const featured = useMemo(
    () =>
      POSTS.reduce((newest, p) =>
        p.dateISO > newest.dateISO ? p : newest
      ),
    []
  );
  const featuredIdx = POSTS.findIndex((p) => p.slug === featured.slug);
  const showFeaturedInGrid = activeCat !== "All" || query.trim() !== "";

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="blog-page">
      <Nav />

      {/* Hero masthead */}
      <section className="pt-[120px] md:pt-[160px] pb-10 md:pb-16 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="flex items-center justify-between border-t border-b border-line py-3 mb-10 md:mb-14 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2"
          >
            <Link
              to="/"
              data-testid="blog-back-home"
              className="inline-flex items-center gap-2 hover:text-signal transition-colors"
            >
              <ArrowLeft size={12} /> Home
            </Link>
            <span>The Digital Marketer · Writing</span>
            <span className="hidden md:inline">{POSTS.length} essays</span>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.05 }}
              className="md:col-span-8"
            >
              <p className="font-mono text-[11px] md:text-xs uppercase tracking-[0.22em] text-signal mb-6">
                ◆ The Journal
              </p>
              <h1
                data-testid="blog-heading"
                className="font-heading font-black text-[56px] sm:text-[88px] md:text-[124px] leading-[0.88] tracking-tightest"
              >
                Writing<span className="text-signal">.</span>
              </h1>
              <p className="mt-6 md:mt-8 text-base md:text-xl text-ink2 leading-relaxed max-w-2xl">
                Field notes on B2B demand, performance marketing, AI, SEO and the slow-motion
                rewiring of how buyers buy. Published when I actually have something to say.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="md:col-span-4 grid grid-cols-2 gap-0 border border-ink"
            >
              <div className="p-6 border-r border-b border-ink">
                <div className="font-heading font-black text-5xl number-tabular leading-none tracking-tightest">
                  {POSTS.length}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  Essays
                </div>
              </div>
              <div className="p-6 border-b border-ink">
                <div className="font-heading font-black text-5xl number-tabular leading-none tracking-tightest">
                  {ALL_CATEGORIES.length}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-ink2">
                  Topics
                </div>
              </div>
              <div className="col-span-2 p-6 bg-ink text-paper">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-paper/60 mb-1">
                  Most recent
                </div>
                <div className="font-heading font-bold text-base leading-tight">
                  {featured.title}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-paper/60">
                  {featured.date}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 md:py-8 border-t border-b border-ink bg-surface sticky top-[72px] z-30 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
          <div className="flex items-center gap-3 flex-wrap" data-testid="category-filters">
            {["All", ...ALL_CATEGORIES].map((c) => {
              const active = c === activeCat;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  data-testid={`cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 border transition-colors ${
                    active
                      ? "bg-ink text-paper border-ink"
                      : "bg-paper text-ink border-ink/30 hover:border-ink"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="relative lg:w-[320px]">
            <Search
              className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-ink2"
              strokeWidth={1.5}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search essays…"
              data-testid="blog-search"
              className="w-full bg-transparent border-b border-ink pl-6 pr-2 py-2 font-mono text-[13px] uppercase tracking-[0.12em] placeholder:text-ink2/60 focus:outline-none focus:border-signal"
            />
          </div>
        </div>
      </section>

      {/* Featured (when no filter applied) */}
      {!showFeaturedInGrid && (
        <section className="py-14 md:py-20 bg-paper">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex items-center gap-4 mb-8 md:mb-12">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">
                ◆ Featured
              </span>
              <span className="flex-1 h-px bg-line" />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
                Editor&apos;s pick
              </span>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7 }}
            >
              <Link
                to={`/blog/${featured.slug}`}
                data-testid="featured-post"
                className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-stretch border border-ink p-6 md:p-8 hover:bg-surface transition-colors"
              >
                <div className="md:col-span-7 min-w-0 relative aspect-[16/10] md:aspect-auto overflow-hidden border border-ink">
                  <BlogCover post={{ ...featured, index: featuredIdx }} />
                </div>
                <div className="md:col-span-5 flex flex-col justify-between">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal mb-4">
                      Latest · {featured.date}
                    </div>
                    <h2 className="font-heading font-black text-3xl md:text-5xl leading-[0.95] tracking-tightest group-hover:text-signal transition-colors">
                      {featured.title}
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-ink2 leading-relaxed">
                      {featured.excerpt}
                    </p>
                  </div>
                  <div className="mt-8 flex items-center justify-between border-t border-line pt-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
                      {featured.readTime}
                    </span>
                    <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] group-hover:text-signal">
                      Read essay <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="pb-20 md:pb-32 bg-paper">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-8 md:mb-12">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">
              § Archive
            </span>
            <span className="flex-1 h-px bg-line" />
            <span
              data-testid="results-count"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2"
            >
              {filtered.length} {filtered.length === 1 ? "essay" : "essays"}
              {activeCat !== "All" ? ` · ${activeCat}` : ""}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div
              data-testid="empty-state"
              className="border border-ink p-12 text-center"
            >
              <div className="font-heading font-black text-3xl md:text-4xl tracking-tightest mb-3">
                No essays match that filter.
              </div>
              <p className="text-ink2 mb-6">Try a different topic or clear the search.</p>
              <button
                onClick={() => {
                  setActiveCat("All");
                  setQuery("");
                }}
                className="inline-flex items-center gap-2 bg-ink text-paper hover:bg-signal px-6 py-3 font-heading font-bold text-[12px] uppercase tracking-[0.2em] transition-colors"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div
              data-testid="blog-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
            >
              {filtered.map((p, i) => (
                <motion.div
                  key={p.slug}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
                  className="blog-card group flex flex-col"
                >
                  <Link to={`/blog/${p.slug}`} data-testid={`post-${p.slug}`} className="flex flex-col">
                    <div className="relative aspect-[16/10] overflow-hidden border border-ink">
                      <BlogCover post={p} compact />
                    </div>
                    <div className="pt-6 flex-1 flex flex-col">
                      <h3 className="font-heading font-bold text-2xl md:text-[26px] leading-[1.15] tracking-tight group-hover:text-signal transition-colors">
                        {p.title}
                      </h3>
                      <p className="mt-4 text-[15px] leading-relaxed text-ink2 flex-1">{p.excerpt}</p>
                      <div className="mt-6 flex items-center justify-between border-t border-line pt-4">
                        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">
                          {p.date} · {p.readTime}
                        </span>
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.22em] group-hover:text-signal">
                          Read <ArrowUpRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-ink text-paper py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal mb-4">
              ◆ Put the ideas to work
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest">
              Liked the thinking? <br />
              <span className="italic font-medium">Let&apos;s apply it to your funnel.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:justify-self-end">
            <Link
              to="/#contact"
              data-testid="blog-cta-contact"
              className="inline-flex items-center gap-3 bg-signal text-paper hover:bg-paper hover:text-ink px-8 py-5 font-heading font-bold text-[13px] uppercase tracking-[0.2em] transition-colors"
            >
              Book a Call <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster
        position="bottom-right"
        offset={104}
        toastOptions={{
          style: {
            background: "#0A0A0A",
            color: "#FDFDFD",
            border: "1px solid #2A2A2A",
            borderRadius: 0,
            fontFamily: "Satoshi, sans-serif",
          },
        }}
      />
    </div>
  );
}
